/**
 * Library → Built-in Agents
 *
 * Platform-level editor for the 9 chain framework prompts:
 *   Inbound  (5): chain_navigation, chain_greeter, chain_verifier,
 *                 chain_intake, chain_closer
 *   Outbound (4): chain_outbound_navigation, chain_outbound_opener,
 *                 chain_recipient_verify, chain_outbound_closer
 *
 * These live in library_prompts with scope="platform-chain". Every
 * sub-tenant forks them at creation time (seedChainPromptsForSubTenant in
 * the repo). Edits here cascade to tenants via the Library Updates page.
 *
 * Unlike Post-Call Actions, the set of keys is FIXED — operators can edit
 * prompt text and version, but not create/delete keys. The server API
 * rejects unknown keys via the CHAIN_PROMPT_KEYS allowlist.
 */

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  PromptEditor,
  VersionHistory,
  Tabs,
  StatusBadge,
  VersionBadge,
} from "../components.jsx";
import { useLibraryEnv } from "../LibraryEnvContext.jsx";

// Map chain-framework keys to their structured agent counterpart at
// /library/agents/{id}. When a row has a structured equivalent, the
// chain-framework prompt is DEPRECATED — clicking "Edit Structured"
// goes to the proper editor at /library/agents/. Editing the plain-text
// version still works (for back-compat) but emits a deprecation banner.
//
// The two navigation templates have NO structured equivalent — they're
// framework prose templates injected universally and stay plain text.
const STRUCTURED_EQUIVALENT = {
  chain_greeter:                "greeter",
  chain_verifier:               "verifier",
  chain_intake:                 "intake",
  chain_closer:                 "closer",
  chain_outbound_opener:        "outbound_opener",
  chain_recipient_verify:       "recipient_verify",
  chain_outbound_intake:        "outbound_intake",
  chain_outbound_closer:        "outbound_closer",
  chain_appointment_reminder:   "appointment_reminder",
  chain_chemo_checkin_24h:      "chemo_checkin_24h",
  chain_lab_results_review:     "lab_results_review",
  chain_medication_refill:      "medication_refill",
  chain_sdoh_followup:          "sdoh_followup",
  chain_insurance_auth_followup:"insurance_auth_followup",
  chain_symptom_checkin:        "symptom_checkin",
  chain_care_plan_review:       "care_plan_review",
  // chain_navigation + chain_outbound_navigation: NOT structured — pure text framework
};

// Key → human-friendly label + short description. Keeps the registry
// in one place so renames flow through the list UI and the editor
// breadcrumb consistently.
const PROMPT_META = {
  chain_navigation:            { label: "Navigation (Inbound)",       agent: "navigation",        desc: "Shared framework injected into every dynamic task agent. Defines the ask-wait-classify exit sequence + queue update logic." },
  chain_greeter:               { label: "Greeter",                    agent: "greeter",           desc: "Opens the call, classifies patient_type (existing/new), hands off to verifier or referral-intake." },
  chain_verifier:              { label: "Verifier",                   agent: "verifier",          desc: "Collects name + DOB, calls verify_patient, unlocks the full tool set." },
  chain_intake:                { label: "Intake",                     agent: "intake",            desc: "After verify, captures the patient's verbatim need as an intent for the orchestrator to route." },
  chain_closer:                { label: "Closer",                     agent: "closer",            desc: "Says goodbye and fires end_call once the task queue is drained." },
  chain_outbound_navigation:   { label: "Navigation (Outbound)",      agent: "navigation",        desc: "Shared framework for outbound task agents. Narrower than inbound — no triage preemption, different anything-else phrasing." },
  chain_outbound_opener:       { label: "Outbound Opener",            agent: "outbound_opener",   desc: "Places the outbound call, confirms recipient identity, announces the call's purpose." },
  chain_recipient_verify:      { label: "Recipient Verify",           agent: "recipient_verify",  desc: "Verifies the person who answered is the expected patient (DOB check) before discussing anything." },
  chain_outbound_closer:       { label: "Outbound Closer",            agent: "outbound_closer",   desc: "Two-phase close for outbound calls: first asks anything-else, then goodbye + end_call." },
};

// Direction grouping — mirrors the server's CHAIN_PROMPT_KEYS_BY_DIRECTION.
const DIRECTION_GROUPS = [
  {
    id: "inbound",
    label: "Inbound",
    description: "Run on calls placed TO the practice (patient calls in).",
    keys: ["chain_navigation", "chain_greeter", "chain_verifier", "chain_intake", "chain_closer"],
  },
  {
    id: "outbound",
    label: "Outbound",
    description: "Run on calls placed FROM the practice (reminders, check-ins, etc.).",
    keys: ["chain_outbound_navigation", "chain_outbound_opener", "chain_recipient_verify", "chain_outbound_closer"],
  },
];

export default function LibraryChainFramework() {
  const { env, isDraft, isLive } = useLibraryEnv();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);       // { key, content, version, is_live, is_draft }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listChainFrameworkPrompts(env);
      setPrompts(res.data || []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [env]);

  useEffect(() => { load(); }, [load]);

  const openEditor = async (key) => {
    setBusy(true);
    try {
      const res = await api.getChainFrameworkPrompt(key, env);
      setEditing({
        key,
        content: res.data?.content || "",
        version: res.data?.version || 0,
        is_live: !!res.data?.is_live,
        is_draft: !!res.data?.is_draft,
      });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const savePrompt = async () => {
    if (!editing) return;
    setBusy(true);
    setError(null);
    try {
      await api.writeChainFrameworkPrompt(editing.key, {
        mode: "draft",
        author: "admin",
        commit_msg: `Edit ${PROMPT_META[editing.key]?.label || editing.key}`,
        content: editing.content,
      });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const publishPrompt = async (key) => {
    setBusy(true);
    try {
      await api.publishChainFrameworkPrompt(key);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const discardDraft = async (key) => {
    if (!window.confirm(`Discard draft changes to "${PROMPT_META[key]?.label || key}"? The live version stays.`)) return;
    setBusy(true);
    try {
      await api.discardChainFrameworkPromptDraft(key);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;

  // ── Editor view ──────────────────────────────────────────────────
  // Mirrors the SubTenant agent edit layout: PageHeader (with Version +
  // Status badges) → Tabs (Configuration / Prompt / History) → tab body.
  if (editing) {
    return (
      <ChainFrameworkAgentEditor
        editing={editing}
        setEditing={setEditing}
        onSave={savePrompt}
        onClose={() => setEditing(null)}
        env={env}
        isLive={isLive}
        isDraft={isDraft}
        busy={busy}
        error={error}
      />
    );
  }

  // ── List view ────────────────────────────────────────────────────
  const promptByKey = Object.fromEntries(prompts.map((p) => [p.key, p]));

  return (
    <>
      <PageHeader
        title="Built-in Agents"
        subtitle="Platform-level chain framework prompts. Most have been migrated to structured agent definitions — edit those at /library/agents/. Only the navigation templates are still managed here."
        breadcrumbs={[
          { label: "Library", to: `/library/${env}/global-rules` },
          { label: "Built-in Agents" },
        ]}
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <div className="card" style={{ background: "#f0f5fa", borderColor: "#b8d4ea", marginBottom: 16 }}>
          <div className="small" style={{ lineHeight: 1.55 }}>
            <strong>Two flavors live here:</strong>
            <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
              <li>
                <span style={{ background: "#e3f2fd", padding: "1px 6px", borderRadius: 3, color: "#0d47a1", fontWeight: 600, fontSize: 11 }}>
                  NAVIGATION
                </span>{" "}
                — <code>chain_navigation</code> and <code>chain_outbound_navigation</code>. Pure text framework
                templates injected into every task agent. Edit here.
              </li>
              <li style={{ marginTop: 4 }}>
                <span style={{ background: "#fff3cd", padding: "1px 6px", borderRadius: 3, color: "#664d03", fontWeight: 600, fontSize: 11 }}>
                  DEPRECATED
                </span>{" "}
                — All other <code>chain_*</code> entries. Replaced by structured agent definitions in
                <code>/library/agents/</code>. The runtime ignores these legacy prompts. Click "Edit Structured →"
                on each row to edit the live version.
              </li>
            </ul>
          </div>
        </div>

        {DIRECTION_GROUPS.map((group) => (
          <div key={group.id} style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "var(--color-text-muted)", marginBottom: 4 }}>
              {group.label}
            </h3>
            <div className="muted small" style={{ marginBottom: 12 }}>{group.description}</div>

            {group.keys.map((key) => {
              const meta = PROMPT_META[key] || { label: key, desc: "" };
              const p = promptByKey[key];
              const exists = !!p;
              const structuredId = STRUCTURED_EQUIVALENT[key];
              const isStructuredAvailable = !!structuredId;
              return (
                <div
                  key={key}
                  className="card"
                  style={{
                    marginBottom: 10,
                    opacity: exists ? 1 : 0.55,
                    borderLeft: `4px solid ${
                      isStructuredAvailable
                        ? "#9e9e9e"  // grey — deprecated chain prompt with structured replacement
                        : (group.id === "inbound" ? "#4A6FA5" : "#A54A6F")
                    }`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>
                        {meta.label}
                        {isStructuredAvailable && (
                          <span
                            className="badge"
                            style={{
                              marginLeft: 8,
                              fontSize: 10,
                              padding: "2px 8px",
                              background: "#fff3cd",
                              border: "1px solid #ffc107",
                              color: "#664d03",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              borderRadius: 3,
                            }}
                          >
                            Deprecated
                          </span>
                        )}
                        {!isStructuredAvailable && (
                          <span
                            className="badge"
                            style={{
                              marginLeft: 8,
                              fontSize: 10,
                              padding: "2px 8px",
                              background: "#e3f2fd",
                              border: "1px solid #90caf9",
                              color: "#0d47a1",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              borderRadius: 3,
                            }}
                          >
                            Navigation
                          </span>
                        )}
                      </div>
                      <div className="muted small mono" style={{ marginTop: 2 }}>
                        <code>{key}</code>
                        {exists && ` · v${p.version}`}
                        {exists && p.is_live && <span style={{ marginLeft: 8, color: "#2e7d32" }}>● live</span>}
                        {exists && p.is_draft && <span style={{ marginLeft: 8, color: "#f57c00" }}>● draft</span>}
                        {!exists && <span style={{ marginLeft: 8, color: "#c62828" }}>● not seeded — run scripts/seed-chain-framework-library.js</span>}
                      </div>
                      <p className="muted small" style={{ marginTop: 6, marginBottom: 0 }}>{meta.desc}</p>
                      {isStructuredAvailable && (
                        <div
                          style={{
                            marginTop: 8,
                            padding: "8px 12px",
                            background: "#fff8e1",
                            border: "1px solid #ffe0a3",
                            borderRadius: 4,
                            fontSize: 12,
                            color: "#5b4400",
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>DEPRECATED</strong> — this plain-text prompt is no longer used by the runtime.
                          Edit the structured definition instead at{" "}
                          <Link
                            to={`/library/built-in-agents/${structuredId}`}
                            style={{ fontFamily: "var(--font-mono)" }}
                          >
                            /library/agents/{structuredId}
                          </Link>
                          . You can still view/edit this legacy prompt, but changes will not affect calls.
                        </div>
                      )}
                      {p?.commit_msg && (
                        <p className="muted small" style={{ marginTop: 4, fontStyle: "italic" }}>
                          Last commit: "{p.commit_msg}"
                        </p>
                      )}
                    </div>
                    <div className="btn-row" style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                      {isStructuredAvailable ? (
                        <Link
                          to={`/library/built-in-agents/${structuredId}`}
                          className="btn primary sm"
                          style={{ whiteSpace: "nowrap", textDecoration: "none", textAlign: "center" }}
                        >
                          Edit Structured →
                        </Link>
                      ) : null}
                      <button
                        className="btn sm"
                        onClick={() => openEditor(key)}
                        disabled={busy || !exists}
                        style={isStructuredAvailable ? { opacity: 0.7 } : {}}
                      >
                        {isStructuredAvailable
                          ? (isLive ? "View legacy" : "Edit legacy")
                          : (isLive ? "View" : "Edit")}
                      </button>
                      {isDraft && exists && p.is_draft && !p.is_live && !isStructuredAvailable && (
                        <button className="btn sm" onClick={() => publishPrompt(key)} disabled={busy}>
                          Publish
                        </button>
                      )}
                      {isDraft && exists && p.is_draft && p.is_live && !isStructuredAvailable && (
                        <button className="btn sm" onClick={() => discardDraft(key)} disabled={busy}>
                          Discard draft
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {prompts.length === 0 && (
          <div className="empty-state">
            <h3>Platform library not seeded</h3>
            <p>
              No built-in agent prompts are present in the library. Run{" "}
              <code>node scripts/seed-chain-framework-library.js</code> on the admin host to populate them
              from the runtime JS defaults.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ChainFrameworkAgentEditor — mirrors the SubTenantPromptEdit layout.
//
// Top-level tabs: Configuration | Prompt | History
//   - Configuration: read-only metadata mirror (name, key, agent, type,
//     description). Same shape as the SubTenant agent's Configuration tab,
//     minus tools/triggers/specialties (chain framework prompts don't have
//     those — they're injected universally into every task agent).
//   - Prompt: the standard PromptEditor component (same one used everywhere).
//   - History: VersionHistory list of prior commits.
// ─────────────────────────────────────────────────────────────────────

function ChainFrameworkAgentEditor({ editing, setEditing, onSave, onClose, env, isLive, isDraft, busy, error }) {
  const [tab, setTab] = React.useState("configuration");
  const [history, setHistory] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const meta = PROMPT_META[editing.key] || { label: editing.key, agent: "", desc: "" };

  // Lazy-load history on first View of the History tab.
  React.useEffect(() => {
    if (tab !== "history") return;
    let cancelled = false;
    setHistoryLoading(true);
    (async () => {
      try {
        const r = await api.getChainFrameworkPromptHistory(editing.key);
        if (!cancelled) setHistory(r.data || []);
      } catch {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, editing.key]);

  const handleSave = async (content) => {
    setEditing({ ...editing, content });
    // Defer to the parent save path which already knows the API + commit_msg.
    // We pass the new content through editing state — onSave reads editing.content.
    // Synchronous setState batches with onSave; await ensures DB write is done.
    await onSave();
  };

  const docForBadges = {
    version: editing.version,
    is_live: editing.is_live,
    is_draft: editing.is_draft,
  };

  return (
    <>
      <PageHeader
        title={meta.label}
        subtitle={`Built-in agent prompt — ${editing.key}`}
        breadcrumbs={[
          { label: "Library", to: `/library/${env}/global-rules` },
          { label: "Built-in Agents", to: `/library/${env}/built-in-agents` },
          { label: meta.label },
        ]}
        actions={
          <>
            <VersionBadge version={editing.version} />
            <StatusBadge doc={docForBadges} />
            <button className="btn sm" onClick={onClose}>Back</button>
          </>
        }
      />

      <Tabs
        tabs={[
          { id: "configuration", label: "Configuration" },
          { id: "prompt", label: "Prompt" },
          { id: "history", label: `History${history.length ? ` (${history.length})` : ""}` },
        ]}
        active={tab}
        onChange={setTab}
      />

      <div className="tab-content">
        <ErrorBanner error={error} />

        {tab === "configuration" ? (
          <ChainFrameworkConfigurationTab
            editing={editing}
            meta={meta}
          />
        ) : tab === "prompt" ? (
          <PromptEditor
            initialContent={editing.content}
            onSave={handleSave}
            busy={busy}
            draftDoc={docForBadges}
            readOnly={isLive}
            hidePublish
          />
        ) : (
          <div className="card">
            {historyLoading ? <Loading /> : <VersionHistory versions={history} />}
          </div>
        )}
      </div>
    </>
  );
}

// Read-only Configuration tab — same shape as SubTenant agent Configuration
// tab, just narrower (no tools / triggers / specialties — chain framework
// prompts are universal templates that don't carry those fields).
function ChainFrameworkConfigurationTab({ editing, meta }) {
  const ReadOnlyField = ({ label, value, multiline = false, mono = false, hint = null }) => (
    <div className="form-field">
      <label>{label}</label>
      <div style={{
        padding: "8px 10px",
        background: "#f6f7f9",
        border: "1px solid #e0e3e8",
        borderRadius: 4,
        fontSize: 13,
        fontFamily: mono ? "var(--font-mono)" : "inherit",
        color: "#222",
        whiteSpace: multiline ? "pre-wrap" : "normal",
        minHeight: 32,
      }}>
        {value || <span className="muted">—</span>}
      </div>
      {hint && <div className="help">{hint}</div>}
    </div>
  );

  return (
    <div className="card">
      <div className="card-header">
        <h3>Basic Information</h3>
        <span className="muted small" style={{ marginLeft: 8 }}>read-only · platform-level</span>
      </div>

      <div style={{
        padding: "10px 14px",
        background: "#eef3fa",
        border: "1px solid #cfd8e3",
        borderRadius: 6,
        fontSize: 12,
        color: "#2E4F72",
        marginBottom: 16,
        lineHeight: 1.55,
      }}>
        <strong>Platform-level chain framework prompt.</strong> These templates
        are forked into every sub-tenant at creation time. Editing the prompt
        here updates the platform default; existing sub-tenants keep their own
        forked copies until you cascade via Library Updates.
      </div>

      <ReadOnlyField label="Name" value={meta.label} />
      <ReadOnlyField label="Description" value={meta.desc} multiline />

      <div className="field-row">
        <div className="form-field" style={{ flex: 1 }}>
          <label>Key</label>
          <div style={{
            padding: "8px 10px", background: "#f6f7f9",
            border: "1px solid #e0e3e8", borderRadius: 4,
            fontSize: 13, fontFamily: "var(--font-mono)",
          }}>
            {editing.key}
          </div>
        </div>
        <div className="form-field" style={{ flex: 1 }}>
          <label>Agent</label>
          <div style={{
            padding: "8px 10px", background: "#f6f7f9",
            border: "1px solid #e0e3e8", borderRadius: 4,
            fontSize: 13, fontFamily: "var(--font-mono)",
          }}>
            {meta.agent || "—"}
          </div>
        </div>
        <div className="form-field" style={{ flex: 1 }}>
          <label>Type</label>
          <div style={{
            padding: "8px 10px", background: "#f6f7f9",
            border: "1px solid #e0e3e8", borderRadius: 4,
            fontSize: 13,
          }}>
            chain-framework
          </div>
        </div>
      </div>

      <ReadOnlyField
        label="Variables available at runtime"
        value="{{practice_name}}, {{context}}, {{patient_name}}"
        mono
        hint="Substituted into the prompt at runtime. Other tenant variables defined in Sub-tenant Settings → Variables also apply."
      />
    </div>
  );
}
